import React from 'react';
import DodTermText from '../DodTermText';
import type { DodGlossaryTermId } from '../dodGlossary';
import {
  inferItemStepProgress,
  verifiedStepCounts,
  type ComplianceChecklistItemDef,
  type ComplianceItemState,
  type ComplianceStepEvidence,
} from '../constants/complianceChecklists';
import { calendarDateLabel, evidenceAsksForADate } from '../complianceCalendar';
import { laneForVerifyStep, workflowLaneLabel } from '../constants/complianceWorkflowGuardrails';
import { taskFileInputId } from './TaskAssist';

const MAX_STORED_EVIDENCE_CHARS = 450_000;

type DodVerifyStepsProps = {
  item: ComplianceChecklistItemDef;
  itemState?: ComplianceItemState;
  language: 'en' | 'es';
  waiting?: boolean;
  seenTerms?: Set<DodGlossaryTermId>;
  showEveryStep?: boolean;
  /** One checkbox per thing still open. Finished work stays checked, with no status chips. */
  plain?: boolean;
  /** Project identity already on file — used to auto-confirm the franchise Given step. */
  projectIdentity?: { legalName?: string; ein?: string };
  onStepProgressChange: (progress: Record<string, ComplianceStepEvidence>) => void;
  calendarDate?: string;
  onCalendarDate?: (date: string) => void;
};

const readAsDataUrl = (file: File): Promise<string> => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read'));
    reader.readAsDataURL(file);
  })
);

const compressImage = (file: File): Promise<string> => (
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(image.width, image.height || 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      URL.revokeObjectURL(objectUrl);
      if (!context) {
        reject(new Error('canvas'));
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.72));
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('image'));
    };
    image.src = objectUrl;
  })
);

const fileToEvidence = async (file: File): Promise<Pick<ComplianceStepEvidence, 'evidenceName' | 'evidenceMime' | 'evidenceDataUrl'>> => {
  const mime = file.type || 'application/octet-stream';
  if (mime.startsWith('image/')) {
    const dataUrl = await compressImage(file);
    return {
      evidenceName: file.name,
      evidenceMime: 'image/jpeg',
      evidenceDataUrl: dataUrl.length <= MAX_STORED_EVIDENCE_CHARS ? dataUrl : undefined,
    };
  }
  if (file.size <= MAX_STORED_EVIDENCE_CHARS * 0.75) {
    const dataUrl = await readAsDataUrl(file);
    return {
      evidenceName: file.name,
      evidenceMime: mime,
      evidenceDataUrl: dataUrl.length <= MAX_STORED_EVIDENCE_CHARS ? dataUrl : undefined,
    };
  }
  return { evidenceName: file.name, evidenceMime: mime };
};

const maskEin = (ein: string): string => {
  const digits = ein.replace(/\D/g, '');
  if (digits.length === 9) {
    return `${digits.slice(0, 2)}-***${digits.slice(5)}`;
  }
  return ein.length > 4 ? `…${ein.slice(-4)}` : ein;
};

const DodVerifySteps: React.FC<DodVerifyStepsProps> = ({
  item,
  itemState,
  language,
  waiting = false,
  seenTerms,
  showEveryStep = false,
  plain = false,
  projectIdentity,
  onStepProgressChange,
  calendarDate = '',
  onCalendarDate,
}) => {
  const steps = item.verifySteps || [];
  const [uploadError, setUploadError] = React.useState('');
  const [showAll, setShowAll] = React.useState(showEveryStep);
  const [openedId, setOpenedId] = React.useState<string | null>(null);
  const identitySynced = React.useRef(false);
  const legalName = String(projectIdentity?.legalName || '').trim();
  const einOnProject = String(projectIdentity?.ein || '').trim();
  const identityReady = Boolean(legalName && einOnProject);

  React.useEffect(() => {
    if (!identityReady || identitySynced.current) return;
    if (item.id !== 'tx-franchise-account-status') return;
    const hasGiven = (item.verifySteps || []).some((step) => step.id === 'given-identity');
    if (!hasGiven) return;
    const stored = itemState?.stepProgress?.['given-identity'];
    if (stored?.done) {
      identitySynced.current = true;
      return;
    }
    identitySynced.current = true;
    const next = inferItemStepProgress(item, itemState);
    onStepProgressChange({
      ...next,
      'given-identity': {
        ...(next['given-identity'] || {}),
        done: true,
        doneAt: new Date().toISOString(),
        inferred: true,
        evidenceNote: `On project: ${legalName} · EIN ${maskEin(einOnProject)}`,
      },
    });
  }, [einOnProject, identityReady, item, itemState, legalName, onStepProgressChange]);

  if (!steps.length) {
    return null;
  }
  const progress = inferItemStepProgress(item, itemState);
  const counts = verifiedStepCounts(item, itemState);
  const currentId = counts.current?.id;
  const spanish = language === 'es';
  const expanded = plain || showEveryStep || showAll;
  const openId = expanded ? null : (openedId || currentId || steps[0]?.id);
  const paired = steps.length === 2 && steps[0]?.kind === 'do' && steps[1]?.kind === 'proof';
  const samNote = item.id === 'sam-uei-cage';

  const patchStep = (stepId: string, next: ComplianceStepEvidence) => {
    setUploadError('');
    onStepProgressChange({
      ...progress,
      [stepId]: next,
    });
  };

  const partTitleFor = (step: (typeof steps)[number]): string => {
    const text = step.text.trim();
    if (text.length <= 140) {
      return text;
    }
    const first = text.split(/(?<=[.!?])\s+(?=[A-Z“"])/)[0]?.trim();
    if (first && first.length <= 140 && first.length < text.length) {
      return first;
    }
    return step.kind === 'proof' ? step.evidenceHint : item.title;
  };

  if (plain && paired && steps[1]) {
    const proof = steps[1];
    const doing = steps[0];
    const proofRow = progress[proof.id] || { done: false };
    const checked = Boolean(proofRow.done);
    return (
      <ol className="dod-verify is-plain" aria-label={spanish ? 'Todavía por hacer' : 'Still to do'}>
        <li className={`dod-verify-step${checked ? ' is-done' : ' is-open'}`}>
          <label className="dod-verify-check">
            <input
              type="checkbox"
              checked={checked}
              aria-label={proof.text}
              onChange={(event) => {
                const done = event.target.checked;
                const stamped = done ? new Date().toISOString() : undefined;
                onStepProgressChange({
                  ...progress,
                  [doing.id]: { ...(progress[doing.id] || {}), done, doneAt: stamped, inferred: false },
                  [proof.id]: { ...proofRow, done, doneAt: stamped, inferred: false },
                });
              }}
            />
            <span className="dod-verify-copy">
              <DodTermText text={proof.text} language={language} seen={seenTerms} />
            </span>
          </label>
        </li>
      </ol>
    );
  }

  return (
    <div className={`dod-verify${paired ? ' is-paired' : ''}${plain ? ' is-plain' : ''}`}>
      {plain || paired ? null : (
      <p className="dod-verify-summary" role="status">
        <DodTermText
          text={
            counts.done === counts.total
              ? (spanish
                ? `${counts.done} de ${counts.total} pasos verificados`
                : `${counts.done} of ${counts.total} steps verified`)
              : waiting
                ? (spanish
                  ? `${counts.done} de ${counts.total} verificados · en espera de GSA`
                  : `${counts.done} of ${counts.total} verified · waiting on GSA`)
                : (spanish
                  ? `${counts.done} de ${counts.total} pasos verificados`
                  : `${counts.done} of ${counts.total} steps verified`)
          }
          language={language}
          seen={seenTerms}
        />
      </p>
      )}
      {plain || showEveryStep ? null : (
      <p className="dod-decision-how dod-verify-hint">
        {spanish
          ? 'Abra el paso de ahora, adjunte la prueba y márquelo.'
          : 'Open the current step, attach proof, and mark it.'}
      </p>
      )}
      {steps.length > 3 && !showEveryStep ? (
        <button
          type="button"
          className="dashboard-security-inline-btn dod-verify-toggle"
          aria-pressed={showAll}
          onClick={() => setShowAll((value) => !value)}
        >
          {showAll
            ? (spanish ? 'Mostrar solo el paso de ahora' : 'Show only the current step')
            : (spanish ? 'Mostrar todos los pasos' : 'Show every step')}
        </button>
      ) : null}
      <ol
        className="dod-verify-steps"
        aria-label={
          item.id === 'sam-uei-cage'
            ? (waiting
              ? (spanish ? 'Pasos SAM.gov mientras GSA revisa' : 'SAM.gov steps while GSA reviews')
              : (spanish ? 'Pasos SAM.gov' : 'SAM.gov steps'))
            : (spanish ? 'Pasos a verificar' : 'Steps to verify')
        }
      >
        {steps.map((step) => {
          const row = progress[step.id] || { done: false };
          const isDone = Boolean(row.done);
          const partTitle = partTitleFor(step);
          const showEvidence = !(paired && step.kind === 'do');
          const isCurrent = step.id === currentId;
          const isWait = step.kind === 'wait' && !isDone;
          const lane = laneForVerifyStep({
            done: isDone,
            waitingOnExternal: isWait,
            isCurrent,
          });
          const statusLabel = workflowLaneLabel(lane, language);
          const isOpen = expanded || step.id === openId;
          if (!isOpen) {
            return (
              <li key={step.id} className={`dod-verify-step is-compact is-${lane}${isWait ? ' is-wait' : ''}`}>
                <div className="dod-verify-compact">
                  <span className={`dod-verify-chip is-${lane}`}>{statusLabel}</span>
                  <span className="dod-verify-oneline">
                    <DodTermText text={partTitle} language={language} seen={seenTerms} />
                  </span>
                  <button type="button" className="dashboard-security-inline-btn" onClick={() => setOpenedId(step.id)}>
                    {spanish ? 'Abrir' : 'Open'}
                  </button>
                </div>
              </li>
            );
          }
          return (
            <li key={step.id} className={`dod-verify-step is-${lane}${isWait ? ' is-wait' : ''}`}>
              <label className="dod-verify-check">
                <input
                  type="checkbox"
                  checked={isDone}
                  aria-label={partTitle}
                  onChange={(event) => {
                    const done = event.target.checked;
                    patchStep(step.id, {
                      ...row,
                      done,
                      doneAt: done ? new Date().toISOString() : undefined,
                      inferred: false,
                    });
                  }}
                />
                <span className="dod-verify-copy">
                  {paired || plain ? null : <span className={`dod-verify-chip is-${lane}`}>{statusLabel}</span>}
                  <span><DodTermText text={partTitle} language={language} seen={seenTerms} /></span>
                </span>
              </label>
              {showEvidence ? (
              <div className="dod-verify-evidence">
                {step.id === 'given-identity' && identityReady ? (
                  <p className="dod-verify-on-project" role="status">
                    {spanish
                      ? `Ya en el proyecto: ${legalName} · EIN ${maskEin(einOnProject)}`
                      : `Already on this project: ${legalName} · EIN ${maskEin(einOnProject)}`}
                  </p>
                ) : null}
                {step.id === 'given-identity' && !identityReady ? (
                  <p className="dod-verify-on-project is-missing" role="status">
                    {spanish
                      ? 'Falta el nombre legal o el EIN en el proyecto. Complételos arriba o en la tarea EIN del IRS.'
                      : 'Legal name or EIN is missing on the project. Add them above or finish the IRS EIN task first.'}
                  </p>
                ) : null}
                <span className="dod-verify-proof-hint">
                  {spanish ? 'Qué conservar: ' : 'Keep: '}
                  <DodTermText text={step.evidenceHint} language={language} seen={seenTerms} />
                </span>
                {row.evidenceName || row.evidenceNote || row.evidenceDataUrl ? (
                  <p className="dod-verify-attached">
                    {row.evidenceDataUrl && (row.evidenceMime || '').startsWith('image/') ? (
                      <img src={row.evidenceDataUrl} alt="" className="dod-verify-thumb" />
                    ) : null}
                    <span>
                      {row.evidenceName || row.evidenceNote}
                      {row.inferred && !row.evidenceName
                        ? (spanish ? ' (desde las notas del caso)' : ' (from case notes)')
                        : ''}
                    </span>
                  </p>
                ) : null}
                <div className="dod-verify-evidence-controls">
                  {onCalendarDate && evidenceAsksForADate(step.evidenceHint) ? (
                    <div className="dod-verify-date">
                      <label className="dod-verify-note">
                        <span>{calendarDateLabel(step.evidenceHint, language)}</span>
                        <input
                          type="date"
                          value={calendarDate}
                          aria-label={calendarDateLabel(step.evidenceHint, language)}
                          onChange={(event) => onCalendarDate(event.target.value)}
                        />
                      </label>
                      {calendarDate ? (
                        <p className="dod-verify-attached">
                          {spanish ? 'En el Calendario de cumplimiento.' : 'On the Compliance Calendar.'}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <label className="dod-verify-upload">
                    <span>{spanish ? 'Añadir un archivo' : 'Add a file'}</span>
                    <input
                      id={taskFileInputId(item.id, step.id)}
                      type="file"
                      accept="image/*,.pdf,application/pdf"
                      aria-label={spanish ? `Añadir un archivo: ${step.evidenceHint}` : `Add a file: ${step.evidenceHint}`}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file) {
                          return;
                        }
                        void fileToEvidence(file).then((evidence) => {
                          patchStep(step.id, {
                            ...row,
                            done: true,
                            doneAt: new Date().toISOString(),
                            inferred: false,
                            evidenceNote: row.evidenceNote,
                            ...evidence,
                          });
                        }).catch(() => {
                          setUploadError(spanish
                            ? 'No se pudo leer ese archivo. Use una captura o un PDF pequeño.'
                            : 'Could not read that file. Use a screenshot or a small PDF.');
                        });
                      }}
                    />
                  </label>
                  <label className="dod-verify-note">
                    <span className="visually-hidden">
                      {spanish ? `Nota de prueba: ${step.evidenceHint}` : `Proof note: ${step.evidenceHint}`}
                    </span>
                    <input
                      type="text"
                      value={row.evidenceNote || ''}
                      placeholder={samNote
                        ? (spanish ? 'Número INC-, enlace o nota…' : 'INC- number, link, or note…')
                        : (spanish ? 'Nota o enlace…' : 'Note or link…')}
                      onChange={(event) => {
                        const evidenceNote = event.target.value;
                        patchStep(step.id, {
                          ...row,
                          evidenceNote,
                          done: row.done || Boolean(evidenceNote.trim()),
                          doneAt: row.doneAt || (evidenceNote.trim() ? new Date().toISOString() : undefined),
                          inferred: false,
                        });
                      }}
                    />
                  </label>
                </div>
                {isWait && item.waitingSteps?.length ? (
                  <ul className="dod-verify-wait-hints">
                    {item.waitingSteps.map((hint) => (
                      <li key={hint}><DodTermText text={hint} language={language} seen={seenTerms} /></li>
                    ))}
                  </ul>
                ) : null}
              </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {uploadError ? <p className="dod-verify-error" role="alert">{uploadError}</p> : null}
    </div>
  );
};

export default DodVerifySteps;
